import { InjectionToken } from '@angular/core';
import { ContentInterface, ContextInterface, EduContent, ResultInterface } from '@campus/dal';
import { StudentTaskWithContentInterface } from '../../../interfaces';
import { ContentActionInterface } from '../content-action.interface';

export type ContentActionType =
  | 'openEduContentAsExercise'
  | 'openEduContentAsSolution'
  | 'openEduContentAsPreview'
  | 'openEduContentAsStream'
  | 'openEduContentAsDownload'
  | 'openEduContentAsWebApp'
  | 'openEduContentAsLink'
  | 'openBoeke'
  | 'openPaperExercise'
  | 'openPaperExerciseAsSolution'
  | 'openEduContentForReview'
  | 'openEduContentForGrading'
  | 'openEduContentAsHtml'
  | 'openInstructions'
  | 'continueEduContentAsExercise'
  | 'retryEduContentAsExercise'
  | 'openEduContentAsStreamCompleted'
  | 'openEduContentAsDownloadCompleted'
  | 'openEduContentAsLinkCompleted'
  | 'openEduContentAsWebAppCompleted'
  | 'openEduFileAsDownload'
  | 'removeFromTask'
  | 'addToTask'
  | 'openLink';

export type ContentActionDictionary = Partial<Record<ContentActionType, ContentActionInterface>>;
export type PartialContentActionDictionary = Partial<Record<ContentActionType, Partial<ContentActionInterface>>>;

export type ContentOpenerHandlerFunctionType = (
  eduContent: EduContent,
  options: OpenContentOptions,
  context?: ContextInterface,
  ...args: any[]
) => void;
export type ContentOpenerHandlerFunctionOptionalType = (
  eduContent: EduContent,
  options?: OpenContentOptions,
  ...args: any[]
) => void;

export type ContentOpenerHandlerParamsType = Parameters<ContentOpenerHandlerFunctionType>;

export type ContentOpenerMethodsType = keyof ContentOpenerInterface;

export interface ContentOpenerNewInterface {
  openEduContentAsExercise: ContentOpenerHandlerFunctionType;
  openEduContentAsSolution?: ContentOpenerHandlerFunctionType;
  openEduContentAsStream: ContentOpenerHandlerFunctionType;
  openEduContentAsDownload: ContentOpenerHandlerFunctionType;
  openEduContentAsLink: ContentOpenerHandlerFunctionType;
  openBoeke: ContentOpenerHandlerFunctionType;
  openEduContentForReview: ContentOpenerHandlerFunctionType;
  openEduContentForGrading?: ContentOpenerHandlerFunctionType;
  openPaperExercise: ContentOpenerHandlerFunctionType;
  openPaperExerciseAsSolution: ContentOpenerHandlerFunctionOptionalType;
  openEduContentAsHtml: ContentOpenerHandlerFunctionType;
  openInstructions: ContentOpenerHandlerFunctionType;
  openEduFileAsDownload: ContentOpenerHandlerFunctionType;
  openLink: ContentOpenerHandlerFunctionType;
}
export interface ContentOpenerInterface {
  openEduContentAsExercise?: ContentOpenerHandlerFunctionType;
  openEduContentAsSolution?: ContentOpenerHandlerFunctionOptionalType;
  openEduContentAsStream?: ContentOpenerHandlerFunctionOptionalType;
  openEduContentAsDownload?: ContentOpenerHandlerFunctionOptionalType;
  openEduContentAsLink?: ContentOpenerHandlerFunctionOptionalType;
  openBoeke?: ContentOpenerHandlerFunctionOptionalType;
  openEduContentForReview?: ContentOpenerHandlerFunctionType;
  openEduContentForGrading?: ContentOpenerHandlerFunctionOptionalType;
  openPaperExercise?: ContentOpenerHandlerFunctionOptionalType;
  openPaperExerciseAsSolution?: ContentOpenerHandlerFunctionOptionalType;
  openEduContentAsHtml?: ContentOpenerHandlerFunctionOptionalType;
  openInstructions?: ContentOpenerHandlerFunctionOptionalType;
  openEduFileAsDownload?: ContentOpenerHandlerFunctionOptionalType;
  openLink?: ContentOpenerHandlerFunctionOptionalType;
}

export interface OpenContentOptions {
  eduFileId?: number;
  linkId?: number;
  /**
   * Only relevant for when a student opens exercises.
   */
  withFeedback?: boolean;
  isRepeatable?: boolean;
  forResult?: ResultInterface;
  inTocId?: number; // deprecated, use context instead
  disableMathjax?: boolean;
  bookId?: number; // deprecated, use context instead
  taskId?: number; // deprecated, use context instead
}

export interface ResultOpenerInterface {
  openEduContentFromResult(result: ResultInterface): void;
}

export const CONTENT_OPEN_ACTIONS_SERVICE_TOKEN = new InjectionToken<ContentOpenActionsServiceInterface>(
  'ContentOpenActionsService'
);

export const CONTENT_OPENER_TOKEN = new InjectionToken<ContentOpenerInterface>('ContentOpener');
export const STUDENT_CONTENT_OPENER_TOKEN = new InjectionToken<ContentOpenerInterface>('StudentContentOpener');

export interface ActionsForTaskInstanceEduContentOptionsInterface {
  isRepeatable?: boolean;
  withFeedback?: boolean;
}

export interface GetActionsForContentConfigInterface<T = ContentActionInterface> {
  filterFn?: (action: ContentActionInterface) => boolean;
  mapFn?: (action: ContentActionInterface) => T;
  sortFn?: (a: T, b: T) => number;
}
export interface ContentOpenActionsServiceInterface {
  getActionsForContent(
    content: ContentInterface,
    result?: ResultInterface,
    options?: {
      forReview?: boolean;
      forDWB?: boolean;
    },
    openContentOptions?: OpenContentOptions
  ): ContentActionInterface[];
  getActionsForContentNew?<T = ContentActionInterface>(
    content: ContentInterface,
    results?: ResultInterface[],
    openContentOptions?: OpenContentOptions,
    context?: ContextInterface,
    config?: GetActionsForContentConfigInterface<T>
  ): T[];
  getActionForContent(content: ContentInterface, actionType: ContentActionType): ContentActionInterface;
  getActionsForTaskInstanceEduContent(
    eduContent: EduContent,
    result: Pick<ResultInterface, 'status'>,
    taskInstance: Pick<StudentTaskWithContentInterface, 'end' | 'isFinished'>,
    options?: ActionsForTaskInstanceEduContentOptionsInterface
  ): ContentActionInterface[];
  getActionsForTaskInstanceEduContentNew?(
    eduContent: EduContent,
    result: Pick<ResultInterface, 'status'>,
    taskInstance: Pick<StudentTaskWithContentInterface, 'end' | 'isFinished'>,
    options?: ActionsForTaskInstanceEduContentOptionsInterface,
    openContentOptions?: OpenContentOptions
  ): ContentActionInterface[];
}
